import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { closeOnOtherPanel } from '../lib/appPanels';
import { Bell, CheckCheck, AlertTriangle, Megaphone, HardHat, Newspaper, Theater, Trophy, Users, Sun, X, Droplets, ShieldAlert, MapPin, HeartHandshake } from 'lucide-react';
import { notificationsAPI } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { EmptyState, ErrorState } from './states';
import {
  loadReadIds, saveReadIds, mergeInbox, unreadBroadcasts, markRead, markAll, safeLink, timeAgo,
} from '../lib/notificationInbox';

const CATEGORY_ICONS = {
  alertas_municipales: Megaphone,
  alerta_rios: Droplets,
  mis_incidencias: AlertTriangle,
  incidencias_nuevas: MapPin,
  vecinos: Users,
  ayuda_vecinal: HeartHandshake,
  obras_cortes: HardHat,
  boletines: Newspaper,
  cultura_eventos: Theater,
  deporte_local: Trophy,
  resumen_diario: Sun,
  moderacion: ShieldAlert,
};

const CATEGORY_COLORS = {
  alertas_municipales: '#dc2626',
  alerta_rios: '#3B82F6',
  mis_incidencias: '#f59e0b',
  incidencias_nuevas: '#d97706',
  vecinos: '#0F766E',
  ayuda_vecinal: '#0F766E',
  obras_cortes: '#ea580c',
  boletines: '#2563eb',
  cultura_eventos: '#7c3aed',
  deporte_local: '#059669',
  resumen_diario: '#0F766E',
  moderacion: '#b45309',
};

// The bell is for everyone: signed out (web or app), it lists the town-wide pushes of the last
// seven days (rivers, municipal notices, new incidencias and help requests); signed in, also the
// user's own. It used to be drawn only for signed-in users and to list only pushes that had
// reached one of their devices, so a web visitor never saw a flood warning here.
export default function NotificationBell({ user, inApp = false }) {
  const [unreadMine, setUnreadMine] = useState(0);
  const [broadcasts, setBroadcasts] = useState([]);
  const [readIds, setReadIds] = useState(() => loadReadIds());
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const ref = useRef(null);
  const panelRef = useRef(null);
  // Mirrors `open` for openInbox, which is memoised and would otherwise read a stale value: the
  // bell could never close its own inbox, and every tap refetched.
  const openRef = useRef(false);
  const historySeq = useRef(0);
  const navigate = useNavigate();

  const fetchUnread = useCallback(async () => {
    if (!user) return;
    try {
      const res = await notificationsAPI.unreadCount();
      setUnreadMine(res.data?.count || 0);
    } catch { /* expected: user cancelled or unavailable */ }
  }, [user]);

  const fetchBroadcasts = useCallback(async () => {
    try {
      const res = await notificationsAPI.broadcasts(20);
      const list = Array.isArray(res.data) ? res.data : [];
      setBroadcasts(list);
      return list;
    } catch {
      return null;
    }
  }, []);

  // The bell is in the header on every route, so this poller runs for the whole session — and it
  // ran while the tab was in the background too, which on a phone means a request every 30s with
  // the screen off. Stop when hidden, fetch once on return so the count is never stale. The town
  // list changes a few times a day: fetched on load and on return to the tab, not every 30 s.
  useEffect(() => {
    let interval = null;
    const start = () => {
      if (interval) clearInterval(interval);
      if (user) interval = setInterval(fetchUnread, 30000);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') { fetchUnread(); fetchBroadcasts(); start(); }
      else if (interval) { clearInterval(interval); interval = null; }
    };
    fetchUnread();
    fetchBroadcasts();
    start();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (interval) clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchUnread, fetchBroadcasts, user]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) { openRef.current = false; setOpen(false); }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistory = useCallback(async () => {
    const mine = ++historySeq.current;
    setLoading(true);
    setLoadError(false);
    const [personal, town] = await Promise.all([
      user ? notificationsAPI.history(15).then((r) => r.data || []).catch(() => null) : Promise.resolve([]),
      fetchBroadcasts(),
    ]);
    if (mine !== historySeq.current) return;
    if (personal === null && town === null) {
      // Not "Sin notificaciones": that would claim the inbox is empty when it just didn't load.
      setLoadError(true);
    } else {
      setItems(mergeInbox(personal || [], town || [], loadReadIds()));
    }
    setLoading(false);
  }, [user, fetchBroadcasts]);

  const closeInbox = useCallback(() => { openRef.current = false; setOpen(false); }, []);

  const openInbox = useCallback(() => {
    const opening = !openRef.current;
    openRef.current = opening;
    setOpen(opening);
    if (opening) loadHistory();
  }, [loadHistory]);

  // Escape closes the inbox.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeInbox(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, closeInbox]);

  // From 640 px the panel hangs from the bell's right edge; where the bell is not near the right
  // (tablet header, Modo Sénior's larger rem) it ran off the left of the screen. Nudge it back in.
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!open || !el) return;
    el.style.transform = '';
    const left = el.getBoundingClientRect().left;
    if (left < 8) el.style.transform = `translateX(${Math.ceil(8 - left)}px)`;
  }, [open, loading, items]);

  const rememberRead = (next) => {
    setReadIds(next);
    saveReadIds(next);
  };

  const markAllRead = async () => {
    const { items: after, readIds: next } = markAll(items, readIds);
    setItems(after);
    rememberRead(next);
    if (user && unreadMine > 0) {
      try {
        await notificationsAPI.markAllRead();
        setUnreadMine(0);
      } catch { /* expected: user cancelled or unavailable */ }
    }
  };

  const handleNotifClick = (item) => {
    if (!item.read) {
      const { items: after, readIds: next } = markRead(items, item, readIds);
      setItems(after);
      if (item.kind === 'broadcast') {
        rememberRead(next);
      } else {
        // Personal items are read on the server (markRead existed and was never called).
        setUnreadMine((n) => Math.max(0, n - 1));
        notificationsAPI.markRead(item.id).catch(() => { /* the next unread-count fetch corrects it */ });
      }
    }
    const link = safeLink(item.deepLink);
    if (link) navigate(link);
    closeInbox();
  };

  // In the app the web header is display:none, so this instance is mounted outside it with the
  // trigger hidden, and the native top bar's bell opens it through this event — the same pattern
  // as somostorre:open-search and somostorre:open-a11y. Signed out it opens too: the town-wide
  // list is for everyone (it used to offer sign-in instead).
  useEffect(() => {
    window.addEventListener('somostorre:open-notifications', openInbox);
    return () => window.removeEventListener('somostorre:open-notifications', openInbox);
  }, [openInbox]);
  useEffect(() => closeOnOtherPanel('notifications', closeInbox), [closeInbox]);

  const unread = unreadMine + unreadBroadcasts(broadcasts, readIds);
  const anyUnread = unread > 0 || items.some((i) => !i.read);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={openInbox}
        aria-label={unread > 0 ? `Notificaciones (${unread} sin leer)` : 'Notificaciones'}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="relative w-10 h-10 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors"
        data-testid="notification-bell"
        style={inApp ? { display: 'none' } : undefined}
      >
        <Bell size={18} aria-hidden="true" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-bold leading-none" data-testid="notification-badge" aria-hidden="true">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          // Counted as open by lib/nativeModalBridge.js, so the app's back closes it (it used to leave the app).
          data-state="open"
          aria-label="Notificaciones"
          className="fixed inset-x-0 top-14 mx-2 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 sm:mx-0 bg-white rounded-xl shadow-2xl border border-slate-200 z-[1200] overflow-hidden"
          data-testid="notification-inbox"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-sm font-bold text-slate-700">Notificaciones</h2>
            <div className="flex items-center gap-1">
              {anyUnread && (
                <button onClick={markAllRead} className="min-h-[36px] px-2 text-xs text-torre-teal font-semibold hover:underline flex items-center gap-1" data-testid="mark-all-read">
                  <CheckCheck size={14} aria-hidden="true" /> Leer todo
                </button>
              )}
              <button onClick={closeInbox} className="w-9 h-9 flex items-center justify-center text-slate-500 hover:text-slate-700" aria-label="Cerrar notificaciones">
                <X size={16} aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="max-h-[70vh] sm:max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center" role="status" aria-label="Cargando notificaciones">
                <div className="w-5 h-5 border-2 border-torre-teal/30 border-t-torre-teal rounded-full animate-spin mx-auto" />
              </div>
            ) : loadError ? (
              <ErrorState
                titulo="No hemos podido cargar las notificaciones"
                onRetry={loadHistory}
                className="py-8"
              />
            ) : items.length === 0 ? (
              <EmptyState
                icono={Bell}
                titulo="Sin notificaciones"
                detalle="Los avisos para toda la ciudad y tus alertas aparecerán aquí"
                className="py-8"
              />
            ) : (
              <ul>
                {items.map((item, i) => {
                  const Icon = CATEGORY_ICONS[item.category] || Bell;
                  const color = CATEGORY_COLORS[item.category] || '#64748b';
                  return (
                    <li key={item.key} className="border-b border-slate-100 last:border-0">
                      <button
                        onClick={() => handleNotifClick(item)}
                        className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${
                          !item.read ? 'bg-torre-teal/[0.04]' : ''
                        }`}
                        data-testid={`notification-item-${i}`}
                      >
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${color}15` }} aria-hidden="true">
                          <Icon size={14} style={{ color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm break-words ${!item.read ? 'font-bold text-slate-800' : 'font-medium text-slate-600'}`}>
                              {item.title}
                            </p>
                            {!item.read && (
                              <>
                                <span className="w-1.5 h-1.5 rounded-full bg-torre-teal shrink-0" aria-hidden="true" />
                                <span className="sr-only">(sin leer)</span>
                              </>
                            )}
                          </div>
                          <p className="text-xs text-slate-600 line-clamp-3 mt-0.5 break-words">{item.body}</p>
                          <p className="text-[11px] text-slate-500 mt-1">
                            {item.kind === 'broadcast' ? 'Para toda la ciudad · ' : ''}{timeAgo(item.sent_at)}
                          </p>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-1.5 border-t border-slate-100 bg-slate-50/50">
            <button
              onClick={() => { navigate(user ? '/perfil?notificaciones=1' : '/preguntas-frecuentes'); closeInbox(); }}
              className="min-h-[36px] text-xs text-torre-teal font-semibold hover:underline w-full text-center"
              data-testid="manage-notifications-link"
            >
              {user ? 'Gestionar preferencias' : 'Qué avisos puedo recibir'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
